import { AdsOrderStatus, OrderStatus } from "@/interface/order";
import { getOrderCreatorDisplayName } from "@/biz/orderCreatorName";
import type {
  DeliveryChannelContent,
  DeliveryContentRound,
  DeliveryContentSubmission,
  OrderV2Channel,
} from "@/interface/orderV2";
import {
  PlatFormEnum,
  PlatformContentEnum,
  getContentType,
  getPlatformContentLabelI18n,
} from "@/pages/CreateCampaign/shared/config";
import { orderManuscriptState, Product_arrived } from "@/components/config";

const LEGACY_CONTENT_TYPE_MAP: Record<string, PlatformContentEnum> = {
  "dedicated video": PlatformContentEnum.YOUTUBE_DEDICATED,
  "youtube dedicated video": PlatformContentEnum.YOUTUBE_DEDICATED,
  "integrated video": PlatformContentEnum.YOUTUBE_INTEGRATED,
  "youtube integrated video": PlatformContentEnum.YOUTUBE_INTEGRATED,
  reel: PlatformContentEnum.INSTAGRAM_REEL,
  "instagram reel": PlatformContentEnum.INSTAGRAM_REEL,
  "carousel post": PlatformContentEnum.INSTAGRAM_CAROUSEL_POST,
  "instagram carousel post": PlatformContentEnum.INSTAGRAM_CAROUSEL_POST,
  "tiktok video": PlatformContentEnum.TIKTOK_VIDEO,
  "short video": PlatformContentEnum.TIKTOK_VIDEO,
  "twitter post": PlatformContentEnum.TWITTER_POST,
  "x post": PlatformContentEnum.TWITTER_POST,
  "linkedin post": PlatformContentEnum.LINKEDIN_POST,
  "image post": PlatformContentEnum.LINKEDIN_IMAGE_POST,
  "video post": PlatformContentEnum.LINKEDIN_VIDEO_POST,
};

// The API uses shared names such as Post and Video across platforms.
const PLATFORM_CONTENT_TYPE_MAP: Partial<
  Record<PlatFormEnum, Record<string, PlatformContentEnum>>
> = {
  [PlatFormEnum.INSTAGRAM]: {
    reels: PlatformContentEnum.INSTAGRAM_REEL,
    post: PlatformContentEnum.INSTAGRAM_CAROUSEL_POST,
  },
  [PlatFormEnum.TIKTOK]: {
    video: PlatformContentEnum.TIKTOK_VIDEO,
  },
  [PlatFormEnum.TWITTER]: {
    post: PlatformContentEnum.TWITTER_POST,
  },
  [PlatFormEnum.LINKEDIN]: {
    post: PlatformContentEnum.LINKEDIN_POST,
    "image & text": PlatformContentEnum.LINKEDIN_IMAGE_POST,
    video: PlatformContentEnum.LINKEDIN_VIDEO_POST,
  },
};

export const normalizeOrderPlatform = (
  value: unknown,
): PlatFormEnum | undefined => {
  if (value === PlatFormEnum.X) return PlatFormEnum.TWITTER;
  return Object.values(PlatFormEnum).includes(value as PlatFormEnum)
    ? (value as PlatFormEnum)
    : undefined;
};

export const normalizeOrderContentType = (
  value: unknown,
  platformValue: unknown,
): PlatformContentEnum | undefined => {
  if (
    Object.values(PlatformContentEnum).includes(value as PlatformContentEnum)
  ) {
    return value as PlatformContentEnum;
  }

  if (typeof value === "string") {
    const platform = normalizeOrderPlatform(platformValue);
    const contentType = value.trim().toLowerCase();
    const mapped =
      (platform ? PLATFORM_CONTENT_TYPE_MAP[platform]?.[contentType] : undefined) ??
      LEGACY_CONTENT_TYPE_MAP[contentType];
    if (mapped) return mapped;
  }

  return undefined;
};

export const getOrderChannels = (order: any): OrderV2Channel[] =>
  Array.isArray(order?.channels) ? order.channels : [];

const isOrderAwaitingApplication = (order: any) => {
  const status = order?.__orderStatus ?? order?.status;
  return status === AdsOrderStatus.PendingSelection || status === AdsOrderStatus.QuoteRequested;
};

const hasPlatformPlacements = (platform: PlatFormEnum | undefined, placements: string[]) =>
  !!platform && Object.values(PlatformContentEnum).some(
    (value) => value.startsWith(platform) && placements.includes(value),
  );

export const getOrderPlacementLabel = (
  order: any,
  t: any,
  placements: string[] = order?.availableAhaPicksOfferPlacements ?? [],
) => {
  const platform = normalizeOrderPlatform(order?.platform);
  const contentType = normalizeOrderContentType(order?.contentType, platform);
  if (isOrderAwaitingApplication(order)) {
    if (!hasPlatformPlacements(platform, placements)) return null;
    return getContentType({
      ...order, platform, contentType: undefined, interestedPriceFields: [],
      availableAhaPicksOfferPlacements: placements,
    }, t);
  }
  return (contentType && getPlatformContentLabelI18n(contentType, t)) ||
    getContentType({ ...order, platform }, t);
};

export const buildExpectedDeliveryRevisionChannels = (order: any) => {
  const seen = new Set<string>();
  const channels = getOrderChannels(order).flatMap((channel) => {
    if (
      !channel || typeof channel.channelKey !== "string" || !channel.channelKey.trim() ||
      (typeof channel.deliveryRevision !== "number" && typeof channel.deliveryRevision !== "string") ||
      String(channel.deliveryRevision).trim() === ""
    ) return [];
    const revision = Number(channel.deliveryRevision);
    if (
      seen.has(channel.channelKey) ||
      !Number.isInteger(revision) ||
      revision < 0
    ) {
      return [];
    }
    seen.add(channel.channelKey);
    return [
      {
        channelKey: channel.channelKey,
        expectedDeliveryRevision: revision,
      },
    ];
  });
  return channels;
};

export const buildPlatformDeliverablesFromOrder = (order: any, placements: string[] = []) => {
  if (isOrderAwaitingApplication(order)) {
    return buildOrderChannelRows(order).flatMap((channel) => {
      const platform = normalizeOrderPlatform(channel.platform);
      return platform ? [{
        platform,
        contentTypes: [],
        ...(hasPlatformPlacements(platform, placements)
          ? { placement: { ...channel, availableAhaPicksOfferPlacements: placements } }
          : {}),
      }] : [];
    });
  }

  return buildPlatformDeliverablesFromOrderChannels(order);
};

// 展示订单实际交付类型，不依赖活动配置或订单状态。
export const buildPlatformDeliverablesFromOrderChannels = (order: any) => {
  const grouped = new Map<PlatFormEnum, PlatformContentEnum[]>();

  for (const channel of getOrderChannels(order)) {
    const platform = normalizeOrderPlatform(channel.platform);
    if (!platform) continue;
    const contentType = normalizeOrderContentType(
      channel.contentType,
      platform,
    );
    const contentTypes = grouped.get(platform) ?? [];
    if (contentType) contentTypes.push(contentType);
    grouped.set(platform, contentTypes);
  }

  return Array.from(grouped, ([platform, contentTypes]) => ({
    platform,
    contentTypes,
  }));
};

const normalizeDetailStatus = (detail: any): string => {
  if (detail?.status === OrderStatus.UnderProduction) {
    return String(detail?.contentStage).toLowerCase() === "script"
      ? AdsOrderStatus.UnderScriptProduction
      : AdsOrderStatus.UnderContentProduction;
  }
  if (detail?.status === OrderStatus.UnderReview) {
    return String(detail?.contentStage).toLowerCase() === "script"
      ? AdsOrderStatus.UnderScriptReview
      : AdsOrderStatus.UnderContentReview;
  }
  if (detail?.status === OrderStatus.PendingPublish) {
    return AdsOrderStatus.AdsPendingPublish;
  }
  return detail?.status;
};

export const normalizeOrderV2Detail = (raw: any) => {
  if (!raw || typeof raw !== "object") return raw;
  const channels = Array.isArray(raw.channels) ? raw.channels : [];
  const hasChannels = channels.length > 0;
  const displayChannel =
    channels.find(
      (channel: OrderV2Channel) =>
        channel.channelKey === raw.displayChannelKey,
    );
  const normalizedStatus = normalizeDetailStatus(raw);

  return {
    ...raw,
    ...(normalizedStatus !== raw.status
      ? { originalStatus: raw.status, status: normalizedStatus }
      : {}),
    channels,
    selectableChannels: Array.isArray(raw.selectableChannels)
      ? raw.selectableChannels
      : [],
    platform: normalizeOrderPlatform(raw.platform) ?? raw.platform,
    userName: hasChannels ? displayChannel?.userName : raw.userName,
    creatorProfileLink: hasChannels
      ? displayChannel?.profileLink
      : raw.creatorProfileLink,
    creatorProfilePictureLink: hasChannels
      ? displayChannel?.profilePictureLink
      : raw.creatorProfilePictureLink,
    audienceView: hasChannels
      ? displayChannel?.audienceView
      : raw.audienceView,
    contentType:
      normalizeOrderContentType(
        hasChannels ? displayChannel?.contentType : raw.contentType,
        hasChannels ? displayChannel?.platform : raw.platform,
      ),
    deliveryRevision: hasChannels
      ? displayChannel?.deliveryRevision
      : raw.deliveryRevision,
    detailVM: {
      ...(raw.detailVM ?? {}),
      keyMetrics: {
        ...(raw.detailVM?.keyMetrics ?? {}),
        followerCount:
          raw.detailVM?.keyMetrics?.followerCount ?? raw.followers,
        medianViews:
          raw.detailVM?.keyMetrics?.medianViews ?? raw.medianView,
        medianCpm: raw.detailVM?.keyMetrics?.medianCpm ?? raw.medianCpm,
      },
    },
  };
};

export const buildOrderChannelRows = (order: any) =>
  getOrderChannels(order).map((channel) => {
    const platform =
      normalizeOrderPlatform(channel.platform) ?? channel.platform;
    const contentType = normalizeOrderContentType(
      channel.contentType,
      platform,
    );
    const price = Number(channel.price);
    const directMedianCpm = Number(channel.medianCpm);
    const medianCpm = Number.isFinite(directMedianCpm)
      ? directMedianCpm
      : undefined;

    return {
      availableAhaPicksOfferPlacements: order?.availableAhaPicksOfferPlacements,
      ...channel,
      id: order?.id,
      campaignId: order?.campaignId,
      __orderStatus: order?.status,
      channelKey: channel.channelKey,
      platform,
      contentType,
      creatorLanguage: channel.language,
      creatorCountry: channel.country,
      creatorProfileLink: channel.profileLink,
      creatorProfilePictureLink: channel.profilePictureLink,
      creatorName: channel.displayName,
      userName: channel.userName,
      price: Number.isFinite(price) ? price : undefined,
      medianCpm,
    };
  });

// A single channel is displayed on the order row. Copy only channel display
// fields so order identity, prices, status and action permissions stay intact.
export const buildOrderTableRow = (order: any) => {
  if (getOrderChannels(order).length !== 1) return order;
  const [channel] = buildOrderChannelRows(order);
  return {
    ...order,
    platform: channel.platform,
    creatorName: getOrderCreatorDisplayName(order),
    userName: channel.userName,
    creatorProfileLink: channel.creatorProfileLink,
    creatorProfilePictureLink: channel.creatorProfilePictureLink,
    availableAhaPicksOfferPlacements: channel.availableAhaPicksOfferPlacements,
    contentType: channel.contentType,
    contentGenre: channel.contentGenre,
    creatorLanguage: channel.creatorLanguage,
    creatorCountry: channel.creatorCountry,
    followers: channel.followers,
    medianView: channel.medianView,
    medianCpm: channel.medianCpm,
    predictedCpm: channel.predictedCpm,
    predictedCpmRange: channel.predictedCpmRange,
  };
};

const mapDeliveryStage = (round: DeliveryContentRound): string => {
  const stage = String(round.stage).toUpperCase();
  if (stage === "DRAFT") {
    const version = Math.min(Math.max(Number(round.version) || 1, 1), 5);
    return `Draft_${version}`;
  }
  if (stage === "FINAL") return orderManuscriptState.Final;
  if (stage === "SCRIPT") return orderManuscriptState.Script;
  return round.stage;
};

const mapDeliveryState = (state: string): string => {
  const normalized = String(state).toUpperCase();
  if (normalized === "FEEDBACK_PROVIDED") return "Feedback_provided";
  if (normalized === "APPROVED") return "Approved";
  if (normalized.includes("REVIEW")) return "Under_review";
  if (normalized === "SKIPPED") return "Skipped";
  if (normalized === "SUBMITTED") return "Submitted";
  return state;
};

// Submission revisions are independent of the channel's deliveryRevision.
// Select by revision so either submission array order produces the same result.
export const getDeliverySubmissionHistory = (
  submissions: DeliveryContentSubmission[] = [],
) =>
  [...submissions].sort(
    (a, b) =>
      a.revision - b.revision ||
      Date.parse(a.submittedAt) - Date.parse(b.submittedAt),
  );

export const getCurrentDeliverySubmission = (
  submissions?: DeliveryContentSubmission[],
) => getDeliverySubmissionHistory(submissions).at(-1);

export const mapDeliverySubmissionToLegacyItem = ({
  channel,
  round,
  submission,
}: {
  channel?: DeliveryChannelContent;
  round: DeliveryContentRound;
  submission: DeliveryContentSubmission;
}) => {
  const content = submission.content ?? {};

  return {
    ...content,
    // Preserve the submission identity separately from round IDs and revisions.
    content_id: submission.contentId,
    contentRoundId: round.contentRoundId,
    stage: mapDeliveryStage(round),
    version: round.version,
    status: mapDeliveryState(round.status),
    submitAt: submission.submittedAt,
    revision: submission.revision,
    timing: channel?.timing ?? round.timing,
    channelKey: channel?.channelKey,
    deliveryRevision: channel?.deliveryRevision,
    platform: normalizeOrderPlatform(channel?.platform) ?? channel?.platform,
    displayName: channel?.displayName,
    adAuthCode: content.adAuthCode ?? content.adAuthorization,
  };
};

const getChannelSubmissionHistory = (
  round: DeliveryContentRound,
  channels: DeliveryChannelContent[],
  displayChannel?: DeliveryChannelContent,
) => {
  const versions = new Map<number, DeliveryChannelContent[]>();
  for (const channel of channels) {
    // Unified submissions share a revision across channels. Keep only real
    // submissions for that revision, without filling gaps from newer content.
    const submissions = new Map(
      getDeliverySubmissionHistory(channel.submissions).map((submission) => [
        submission.revision,
        submission,
      ]),
    );
    for (const [revision, submission] of Array.from(submissions)) {
      const versionChannels = versions.get(revision) ?? [];
      versionChannels.push({ ...channel, submissions: [submission] });
      versions.set(revision, versionChannels);
    }
  }

  return Array.from(versions.entries())
    .sort(([a], [b]) => a - b)
    .slice(0, -1)
    .map(([, versionChannels]) => {
      const channel =
        versionChannels.find(
          (candidate) => candidate.channelKey === displayChannel?.channelKey,
        ) ?? versionChannels[0];
      return {
        ...mapDeliverySubmissionToLegacyItem({
          channel,
          round,
          submission: channel.submissions[0],
        }),
        channels: versionChannels,
      };
    });
};

export const normalizeDeliveryContentRecords = (
  rounds: unknown,
  orderDetail: any,
) => {
  if (!Array.isArray(rounds)) return [];

  const deliveryRounds = rounds as DeliveryContentRound[];
  const shipmentRound = deliveryRounds.find(
    (round) => String(round.stage).toUpperCase() === "PRODUCT_SHIPMENT",
  );
  const shipmentAt = shipmentRound?.shippedAt ?? shipmentRound?.submitAt;

  return deliveryRounds.map((round) => {
    // 物流节点是独立事件，保留后端字段，不套用创作内容 round 的映射。
    if (
      round.stage === orderManuscriptState.Product_shipment ||
      round.stage === Product_arrived
    ) return round;
    const channels = Array.isArray(round.channels) ? round.channels : [];
    const displayChannel =
      channels.find(
        (channel) => channel.channelKey === orderDetail?.displayChannelKey,
      ) ?? channels[0];
    const submissions =
      round.contentScope === "COLLABORATION"
        ? round.submissions
        : displayChannel?.submissions;
    const currentSubmission = getCurrentDeliverySubmission(submissions);
    const base = currentSubmission
        ? mapDeliverySubmissionToLegacyItem({
            channel: displayChannel,
            round,
            submission: currentSubmission,
          })
        : {
            contentRoundId: round.contentRoundId,
            stage: mapDeliveryStage(round),
            version: round.version,
            status: mapDeliveryState(round.status),
          };
    const history = getDeliverySubmissionHistory(submissions)
      .filter((submission) => submission !== currentSubmission)
      .map((submission) => ({
        ...mapDeliverySubmissionToLegacyItem({
          channel: displayChannel,
          round,
          submission,
        }),
        ...(displayChannel
          ? { channels: [{ ...displayChannel, submissions: [submission] }] }
          : {}),
      }));
    const reviews = Array.isArray(round.reviews) ? round.reviews : [];
    const latestApproval = reviews
      .filter((review) => review.action === "APPROVE")
      .sort((a, b) => a.reviewRevision - b.reviewRevision)
      .at(-1);
    const feedbackHistory = reviews
      .filter((review) => review.action === "FEEDBACK")
      .slice()
      .sort((a, b) => a.reviewRevision - b.reviewRevision)
      .map((review) => ({
        ...review,
        editedAt: review.createdAt,
        feedback: (review.feedback ?? []).map((feedback) =>
          typeof feedback === "string" ? { text: feedback } : feedback,
        ),
        suggestion: review.suggestion ?? [],
      }));
    const latestFeedback = feedbackHistory.at(-1);
    // Shipment events are still supplied by order detail, independently of
    // the creator's address submission. Retain them for the existing timeline.
    const addressContent = base.stage === orderManuscriptState.Address
      ? orderDetail?.contents?.find((item: any) => item?.stage === orderManuscriptState.Address)
      : undefined;

    return {
      ...(base.stage === orderManuscriptState.Address ? {
        productShipAt:
          orderDetail?.productShipAt ??
          addressContent?.productShipAt ??
          shipmentAt ??
          orderDetail?.productShipment?.trackingInfo?.shippingDate,
        productArrivalAt: orderDetail?.productArrivalAt ?? addressContent?.productArrivalAt,
      } : {}),
      ...round,
      ...base,
      reviews,
      ...(latestApproval
        ? {
            approveNote: latestApproval.approveNote,
            approveAt: latestApproval.createdAt,
            approveNoteTranslation: latestApproval.creatorTranslation,
          }
        : {}),
      actions: round.actions,
      submissionHistory:
        round.contentScope === "CHANNEL" &&
        (/^Draft_[1-5]$/.test(mapDeliveryStage(round)) ||
          mapDeliveryStage(round) === orderManuscriptState.Final)
          ? getChannelSubmissionHistory(round, channels, displayChannel)
          : history,
      ...(latestFeedback
        ? {
            feedback: latestFeedback.feedback,
            suggestion: latestFeedback.suggestion,
            feedbackHistory,
          }
        : {}),
    };
  });
};
